/* eslint-disable jsx-a11y/anchor-is-valid */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TbHierarchy3 } from 'react-icons/tb';
import { FaMapLocationDot, FaLocationDot } from 'react-icons/fa6';
import { HiBuildingOffice2 } from 'react-icons/hi2';
// import { MdOutlineImageSearch } from 'react-icons/md';
import { FaAngleRight, FaAngleDown, FaLock } from 'react-icons/fa';
import { formatDateUS } from '../../utils/helper';
import PaginatedTable from '../PaginationTable';
import AddressTable from './AddressTable';
import PremiumUpgradeCard from './PremiumUpgradeCard';
import './CompanySummary.css';
import Modal from './Modal';
import { Analytics } from '../../api/analytics';
import { fetchAuthSession } from 'aws-amplify/auth';

/* ---------------- Skeleton Loader ---------------- */

const SkeletonBlock = ({ height = 16, width = "100%" }) => (
  <div className="sk-block" style={{ height, width }} />
);

const CompanySummarySkeleton = () => {
  return (
    <div className="summary-fullpage">

      <div className="top-header">
        <div>
          <SkeletonBlock height={32} width="40%" />
          <div style={{ marginTop: 10 }}>
            <SkeletonBlock height={18} width="200px" />
          </div>
        </div>
      </div>

      <div className="info-grid">
        <SkeletonBlock height={20} />
        <SkeletonBlock height={20} />
        <SkeletonBlock height={20} />
      </div>

      {[1, 2, 3].map((i) => (
        <div key={i} style={{ marginTop: "20px" }}>
          <SkeletonBlock height={40} />
          <div style={{ marginTop: 10 }}>
            <SkeletonBlock height={20} />
            <SkeletonBlock height={20} />
          </div>
        </div>
      ))}
    </div>
  );
};

/* ---------------- Expandable ---------------- */

const ExpandableSection = ({ icon, title, children, downloadOpen }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (downloadOpen) setOpen(true);
  }, [downloadOpen]);

  return (
    <div className="expandable-section">
      <button className="section-header" onClick={() => setOpen(!open)}>
        <span className="expandable-title">
          <span className="expandable-title-icon">
            {icon}
            <span className="space"></span>
            {title}
          </span>
          <span>
            {open ? <FaAngleDown size={25} color="#fff" /> : <FaAngleRight size={25} color="#fff" />}
          </span>
        </span>
      </button>

      {open && <div className="section-content">{children}</div>}
    </div>
  );
};

/* ---------------- MAIN ---------------- */

const CompanySummary = ({ data, downloadOpen }) => {

  const [isPaidUser, setIsPaidUser] = useState(false);

  useEffect(() => {
    const getSession = async () => {
      try {
        const session = await fetchAuthSession();

        const groups =
          session.tokens?.idToken?.payload?.["cognito:groups"] ||
          session.tokens?.accessToken?.payload?.["cognito:groups"] ||
          [];

        console.log("groups:", groups);

        setIsPaidUser(groups.includes("Paywall")); // ✅ THIS is key
      } catch (error) {
        console.error("Error fetching session:", error);
      }
    };

    getSession();
  }, []);

  const {
    glue_id,
    company_name,
    corporate_form,
    registrar_id,
    jurisdiction,
    incorporation_date,
    dissolution_date,
    hierarchy_url_enabled,
    status,
    congressional_district,
    registrar_name,
    registration_url,
    registered_agent,
    entity_url,
    primary_naics,
    alternate_identifiers = {},
    addresses = [],
  } = data;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');


  // ✅ SAFE GUARD + SKELETON
  if (!data) {
    return <CompanySummarySkeleton />;
  }

  const LockIcon = () => (
    <FaLock size={14} style={{ marginLeft: 8, opacity: 0.8 }} />
  );

  const rows = [
    ...(alternate_identifiers.uei || []).map(val => ({ type: 'UEI', value: val })),
    ...(alternate_identifiers.cage_code || []).map(val => ({ type: 'CAGE Code', value: val })),
    ...(alternate_identifiers.cik || []).map(val => ({ type: 'CIK', value: val })),
    ...(alternate_identifiers.ein || []).map(val => ({ type: 'EIN', value: val })),
  ];

  const columns = [
    { Header: 'Identifier Type', accessor: 'type' },
    { Header: 'Identifier Value', accessor: 'value' },
  ];

  const handleClick = async (e) => {
    e.preventDefault();
    try {
      // Analytics.logEvent('registrar_id_clicked', { registrar_id });
      await navigator.clipboard.writeText(registrar_id);
      setModalMessage(`Copied Registrar ID ${registrar_id}`);
    } catch {
      setModalMessage(`ID: ${registrar_id}`);
    } finally {
      setIsModalOpen(true);
    }
  };

  const handleModalClose = () => {
    window.open(registration_url, '_blank');
    setIsModalOpen(false);
  };

  return (
    <>
      <Modal title="" isOpen={isModalOpen} onClose={handleModalClose}>
        <p>{modalMessage}</p>
      </Modal>

      <div className="summary-fullpage">

        <div className="top-header">
          <div>
            <h1 className="companyName">{company_name}</h1>
            <p>
              <strong>
                Glue ID:
                <span className="companyNameLabel">{glue_id}</span>
              </strong>
            </p>
          </div>
        </div>

        <hr />

        <div className="info-grid">
          <div>
            <span className="info-label">Jurisdiction:</span><br />
            <span className="info-value">{jurisdiction || '-'}</span>
          </div>

          <div>
            <span className="info-label">Incorporation Date:</span><br />
            <span className="info-value">{formatDateUS(incorporation_date) || '-'}</span>
          </div>

          <div>
            <span className="info-label">Registrar ID:</span><br />
            <a className="info-value-url" onClick={handleClick}>
              {registrar_id || '-'}
            </a>
          </div>
        </div>

        {/* Registration */}
        <ExpandableSection downloadOpen={downloadOpen} icon={<HiBuildingOffice2 size={20} color="#fff" />} title="Registration Details">
          <table className="data-table">
            <thead>
              <tr>
                <th>Registrar Name</th>
                <th>Corporate Form</th>
                <th>Status</th>
                <th>Dissolution Date</th>
                <th>Registered Agent</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{registrar_name || '-'}</td>
                <td>{corporate_form || '-'}</td>
                <td>{status || '-'}</td>
                <td>{dissolution_date || '-'}</td>
                <td>{registered_agent || '-'}</td>
              </tr>
            </tbody>
          </table>
        </ExpandableSection>

        {/* Hierarchy */}
        <ExpandableSection downloadOpen={downloadOpen} icon={<TbHierarchy3 size={20} color="#fff" />}
          title={
            <span className="premium-title">
              Entity Hierarchy {!isPaidUser && <LockIcon />}
            </span>
          }
        >
          <div style={{ position: "relative", minHeight: isPaidUser ? "auto" : "290px" }}>
            {hierarchy_url_enabled ? (
              <Link to={`/graph/${encodeURIComponent(glue_id)}`} className="info-value-url link-style">
                Graph Hierarchy →
              </Link>
            ) : (
              <span>Graph Hierarchy Not Available</span>
            )}

            {/* Overlay only if NOT admin */}
            {!isPaidUser && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  backdropFilter: "blur(6px)",
                  background: "rgba(255,255,255,0.6)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 2,
                  padding: "20px"
                }}
              >
                <PremiumUpgradeCard />
              </div>
            )}
          </div>
        </ExpandableSection>

        {/* Demographic */}
        <ExpandableSection downloadOpen={downloadOpen} icon={<FaMapLocationDot size={20} color="#fff" />}
          title={
            <span className="premium-title">
              Demographic Data {!isPaidUser && <LockIcon />}
            </span>
          }
        >
          <div style={{ position: "relative", minHeight: isPaidUser ? "auto" : "290px" }}>

            <table className="data-table">
              <thead>
                <tr>
                  <th>Primary NAICS</th>
                  <th>Entity URL</th>
                  <th>Congressional District</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{primary_naics || '-'}</td>
                  <td>{entity_url || '-'}</td>
                  <td>{congressional_district || '-'}</td>
                </tr>
              </tbody>
            </table>

            {/* Overlay only if NOT admin */}
            {!isPaidUser && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  backdropFilter: "blur(6px)",
                  background: "rgba(255,255,255,0.6)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 2,
                  padding: "20px"
                }}
              >
                <PremiumUpgradeCard />
              </div>
            )}
          </div>
        </ExpandableSection>

        {/* Alternate */}
        <ExpandableSection downloadOpen={downloadOpen} icon={<FaLocationDot size={20} color="#fff" />}
          title={
            <span className="premium-title">
              Alternate Identifiers {!isPaidUser && <LockIcon />}
            </span>
          }
        >
          <div style={{ position: "relative", minHeight: isPaidUser ? "auto" : "290px" }}>
            <PaginatedTable columns={columns} rows={rows} />

            {/* Overlay only if NOT admin */}
            {!isPaidUser && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  backdropFilter: "blur(6px)",
                  background: "rgba(255,255,255,0.6)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 2,
                  padding: "20px"
                }}
              >
                <PremiumUpgradeCard />
              </div>
            )}
          </div>
        </ExpandableSection>

        {/* Address */}
        <ExpandableSection downloadOpen={downloadOpen} icon={<FaLocationDot size={20} color="#fff" />} title={
          <span className="premium-title">
            Address Details {!isPaidUser && <LockIcon />}
          </span>
        }>
          <div style={{ position: "relative", minHeight: isPaidUser ? "auto" : "290px" }}>
            {/* ALWAYS render real data */}
            <AddressTable addresses={addresses} />

            {/* Overlay only if NOT admin */}
            {!isPaidUser && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  backdropFilter: "blur(6px)",
                  background: "rgba(255,255,255,0.6)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 2,
                  padding: "20px"
                }}
              >
                <PremiumUpgradeCard />
              </div>
            )}
          </div>
        </ExpandableSection>
      </div >
    </>
  );
};

export default CompanySummary;